import { useEffect, useState } from 'react';
import { api } from './api';

export type MyDept = { key: string; name: string };

// Cache a nivel de módulo: /api/me/departments se pide UNA vez por sesión (lo consumen muchas
// tarjetas del To-Do a la vez). El backend devuelve el departamento default primero.
let cache: MyDept[] | null = null;
let inflight: Promise<MyDept[]> | null = null;

/** Limpia el cache de módulo. Sin esto, en una PC compartida el segundo usuario heredaba los
 *  departamentos del primero mientras no se recargara la página. */
export function clearMyDepartmentsCache(): void { cache = null; inflight = null; }

async function load(): Promise<MyDept[]> {
  if (cache) return cache;
  if (!inflight) {
    inflight = api
      .get<{ departments: Array<{ key: string; name: string; is_default?: boolean }> }>('/api/me/departments')
      .then((r) => { cache = (r.departments || []).map((d) => ({ key: d.key, name: d.name })); return cache; })
      // OJO: un fallo transitorio (red, 401 en el arranque) NO se cachea. Antes quedaba []
      // para toda la sesión → el usuario parecía "sin departamento" y el landing lo mandaba
      // al dashboard de Payments aunque fuera de Claims (reporte del dueño 2026-08-09).
      .catch(() => { inflight = null; return []; });
  }
  return inflight;
}

/** Departamentos del usuario + bandera de carga real. Para el landing por departamento:
 *  sin `loaded` no se distingue "todavía cargando" de "no tiene departamento" (= management). */
export function useMyDepartmentsLoaded(): { depts: MyDept[]; loaded: boolean } {
  const [state, setState] = useState<{ depts: MyDept[]; loaded: boolean }>({ depts: cache ?? [], loaded: cache != null });
  useEffect(() => {
    let alive = true;
    load().then((d) => { if (alive) setState({ depts: d, loaded: true }); });
    return () => { alive = false; };
  }, []);
  return state;
}

/** Departamento DEFAULT del usuario actual (o null si no tiene). Cacheado por sesión. */
export function useMyDefaultDepartment(): MyDept | null {
  const [dept, setDept] = useState<MyDept | null>(cache?.[0] ?? null);
  useEffect(() => {
    let alive = true;
    load().then((d) => { if (alive) setDept(d[0] ?? null); });
    return () => { alive = false; };
  }, []);
  return dept;
}
